import { uiSurface } from "shared/ui/uiSurface"
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import dayjs from "dayjs"
import { FC, useState } from "react"
import { useTranslation } from "react-i18next"

const chartTypes = [
  { value: "daily", label: "dashboard.freq.daily" },
  { value: "weekly", label: "dashboard.freq.weekly" },
  { value: "monthly", label: "dashboard.freq.monthly" },
  { value: "yearly", label: "dashboard.freq.yearly" },
] as const

const chartPlaceholderSx = {
  height: 230,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  bgcolor: "grey.50",
  backgroundImage:
    "repeating-linear-gradient(to right, rgba(0,0,0,0.05) 0, rgba(0,0,0,0.05) 1px, transparent 1px, transparent 28px), repeating-linear-gradient(to bottom, rgba(0,0,0,0.05) 0, rgba(0,0,0,0.05) 1px, transparent 1px, transparent 24px)",
}

const legendItemSx = {
  width: 12,
  height: 12,
  borderRadius: 0.5,
}

export const DashboardLabelsChart: FC = () => {
  const { t } = useTranslation()

  const [frequency, setFrequency] = useState("daily")
  const [fromDate, setFromDate] = useState(
    dayjs().subtract(30, "day").format("YYYY-MM-DD"),
  )
  const [toDate, setToDate] = useState(dayjs().format("YYYY-MM-DD"))
  const [selectedTag, setSelectedTag] = useState("call before")

  return (
    <Card
      {...uiSurface("UI-DLV2-075")}
      data-testid="dashboard-labels-card"
      variant="outlined"
      sx={{ flex: 1, borderRadius: 2 }}
    >
      <CardContent sx={{ p: 2.25 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          {t("dashboard.labels")}
        </Typography>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          sx={{ mb: 1.5 }}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>{t("dashboard.tag")}</InputLabel>
            <Select
              data-testid="dashboard-labels-tag-select"
              value={selectedTag}
              label={t("dashboard.tag")}
              onChange={(e) => setSelectedTag(e.target.value)}
            >
              <MenuItem value="call before">call before</MenuItem>
              <MenuItem value="urgent">urgent</MenuItem>
              <MenuItem value="fragile">fragile</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          sx={{ mb: 1.5 }}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <FormControl size="small" sx={{ minWidth: 92 }}>
            <InputLabel>{t("dashboard.chart")}</InputLabel>
            <Select
              data-testid="dashboard-labels-frequency-select"
              value={frequency}
              label={t("dashboard.chart")}
              onChange={(e) => setFrequency(e.target.value)}
            >
              {chartTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {t(type.label)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            data-testid="dashboard-labels-from-input"
            size="small"
            type="date"
            label={t("dashboard.from")}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            data-testid="dashboard-labels-to-input"
            size="small"
            type="date"
            label={t("dashboard.to")}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            data-testid="dashboard-labels-display-btn"
            variant="contained"
            size="small"
          >
            {t("dashboard.display")}
          </Button>
        </Stack>

        <Box sx={chartPlaceholderSx}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-end"
            sx={{ height: "100%", px: 2, py: 2 }}
          >
            {[35, 52, 46, 64, 58, 42, 69, 55, 61].map((height, index) => (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  borderRadius: 1,
                  height: `${height}%`,
                  bgcolor: "primary.light",
                  opacity: 0.75,
                }}
              />
            ))}
          </Stack>
        </Box>

        <Stack
          direction="row"
          spacing={3}
          justifyContent="center"
          sx={{ mt: 1.5 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ ...legendItemSx, bgcolor: "primary.light" }} />
            <Typography variant="caption" color="text.secondary">
              {t("dashboard.legend.completedOrders")}
            </Typography>
          </Stack>
        </Stack>

        <Box
          sx={{
            mt: 2,
            bgcolor: "primary.50",
            py: 2,
            textAlign: "center",
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" fontWeight={500}>
            0 {t("dashboard.orders")}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  )
}
